/* eslint-disable */
import { useRef, useState, useEffect } from "react";
import {
  faCircleExclamation,
  faTimes,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import logo from "../img/swb-logo-heart.png";
import login from "../img/create-account.png";
import { Link, useNavigate, useLocation } from "react-router-dom";

import axios from "../api/axios";
import useAuth from "../hooks/useAuth";
import moment from "moment";

const EMAIL_REGEX =
  /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,12})$/;
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/;
const REGISTER_URL = "/register";

const CreateAccount = () => {
  const emailRef = useRef();
  const errRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  const { orgDetails, setOrgDetails } = useAuth();

  const referredUser = location.state?.referredUser || null;
  let currentOrgDetails = location.state || orgDetails;
  if (referredUser) {
    currentOrgDetails = location.state.orgDetails || orgDetails;
  }
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [validEmail, setValidEmail] = useState(false);
  const [emailFocus, setEmailFocus] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [pwd, setPwd] = useState("");
  const [validPwd, setValidPwd] = useState(false);
  const [pwdFocus, setPwdFocus] = useState(false);

  const [matchPwd, setMatchPwd] = useState("");
  const [validMatch, setValidMatch] = useState(false);
  const [matchFocus, setMatchFocus] = useState(false);

  const [acceptTerms, setAcceptTerms] = useState(false);

  const [errMsg, setErrMsg] = useState("");

  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [dateErrors, setDateErrors] = useState([]);
  const [dateFocused, setDateFocused] = useState(false);

  const dayRef = useRef();
  const monthRef = useRef();
  const yearRef = useRef();

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!currentOrgDetails) {
      navigate("/create-account/step-1", { replace: true });
    } else if (!orgDetails && currentOrgDetails) {
      setOrgDetails(currentOrgDetails);
    }
  }, [currentOrgDetails, orgDetails, setOrgDetails, navigate]);

  useEffect(() => {
    const result = EMAIL_REGEX.test(email);
    setValidEmail(result);
  }, [email]);

  useEffect(() => {
    const result = PWD_REGEX.test(pwd);
    setValidPwd(result);
    const match = pwd === matchPwd;
    setValidMatch(match);
  }, [pwd, matchPwd]);

  useEffect(() => {
    setErrMsg("");
  }, [name, email, pwd, matchPwd, acceptTerms]);

  const pwdHasValue = pwd.length > 0;
  const pwdChecks = {
    length: pwd.length >= 8 && pwd.length <= 24,
    lower: /[a-z]/.test(pwd),
    upper: /[A-Z]/.test(pwd),
    number: /[0-9]/.test(pwd),
    special: /[!@#$%]/.test(pwd),
  };

  const canSubmit =
    acceptTerms &&
    validPwd &&
    ((name.trim().length > 0 && validEmail && validMatch) ||
      (location?.state?.dependantInfo?.name &&
        location?.state?.dependantInfo?.email &&
        location?.state?.dependantInfo?.date_of_birth));

  const validateDate = (dayVal, monthVal, yearVal) => {
    const errors = [];
    const currentYear = new Date().getFullYear();

    if (dayVal && (parseInt(dayVal) < 1 || parseInt(dayVal) > 31)) {
      errors.push("Day must be between 1 and 31");
    }
    if (monthVal && (parseInt(monthVal) < 1 || parseInt(monthVal) > 12)) {
      errors.push("Month must be between 1 and 12");
    }
    if (yearVal && yearVal.length === 4) {
      const yearNum = parseInt(yearVal);
      if (yearNum > currentYear) {
        errors.push("Year cannot be in the future");
      }
      if (yearNum < 1900) {
        errors.push("Year must be 1900 or later");
      }
    }

    return errors;
  };

  const handleDayChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 2);
    setDay(value);

    if (value.length === 2) {
      monthRef.current?.focus();
    }

    const errors = validateDate(value, month, year);
    setDateErrors(errors);
  };

  const handleMonthChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 2);
    setMonth(value);

    if (value.length === 2) {
      yearRef.current?.focus();
    }

    const errors = validateDate(day, value, year);
    setDateErrors(errors);
  };

  const handleYearChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setYear(value);

    const errors = validateDate(day, month, value);
    setDateErrors(errors);
  };

  const handleKeyDown = (e, field) => {
    if (e.key === "Backspace" && e.target.value === "") {
      if (field === "month") {
        dayRef.current?.focus();
      } else if (field === "year") {
        monthRef.current?.focus();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const v1 = EMAIL_REGEX.test(
      (email || location?.state?.dependantInfo?.email) ?? null,
    );
    const v2 = PWD_REGEX.test(pwd);
    const v3 = pwd === matchPwd;
    const v4 =
      (day && month && year && dateErrors.length === 0) ||
      (location?.state?.dependantInfo?.date_of_birth ? true : false);

    if (
      !v1 ||
      !v2 ||
      !v3 ||
      (!v4 && !location?.state?.dependantInfo?.date_of_birth)
    ) {
      setErrMsg(
        "Please ensure your email is valid, password meets requirements, and passwords match.",
      );
      return;
    }

    if (!acceptTerms) {
      setErrMsg(
        "Please accept the Terms and Conditions and Privacy Policy to continue.",
      );
      errRef.current?.focus();
      return;
    }
    setCreatingAccount(true);
    const formattedDate = `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;

    try {
      const response = await axios
        .post(REGISTER_URL, {
          name: location?.state?.dependantInfo?.name ?? name,
          email: location?.state?.dependantInfo?.email ?? email,
          password: pwd,
          password_confirmation: matchPwd,
          date_of_birth: location?.state?.dependantInfo?.date_of_birth
            ? moment(location.state.dependantInfo.date_of_birth).format('DD/MM/YYYY')
            : formattedDate,
          organisation: currentOrgDetails,
          referredUser: referredUser ? referredUser : null,
          pending_dependant_id: location?.state?.dependantInfo?.id ?? null,
        })
        .then((response) => {
          if (response.data.error) {
            switch (response.data.message) {
              case "Request Pending":
                setErrMsg(
                  "Account Request Already Pending for this Email Address",
                );
                break;
              case "Email Taken":
                setErrMsg("Account with this Email Address Already Exists");
                break;
              default:
                setErrMsg("Error Occurred. Could not Create Account");
                break;
            }
            errRef.current.focus();
          } else {
            setCreatingAccount(false);
            setName("");
            setEmail("");
            setPwd("");
            setMatchPwd("");
            setDay("");
            setMonth("");
            setYear("");
            setOrgDetails(null);
            const statusId = response.data.result?.status_id;
            navigate("/signup-complete", {
              replace: true,
              state: { statusId },
            });
          }
        });
    } catch (error) {
      if (!error?.response) {
        setErrMsg("No Server Response");
      } else if (error.response?.status === 422) {
        setErrMsg("The email provided already exists.");
      } else {
        setErrMsg("Error Occurred. Could not create account.");
      }
      errRef.current.focus();
    }
  };

  return (
    <div
      className="h-screen bg-cover bg-center px-8 relative"
      style={{ backgroundImage: `url(${login})` }}
    >
      <div className="max-w-lg w-full space-y-4 md:space-y-6 sm:p-8 md:absolute md:top-[15%] md:left-[5%] lg:top-[4%] lg:left-[15%]">
        <div className="pt-16 md:pt-0 flex items-center gap-3 pb-2 leading-tight tracking-wider text-[#360F41] dark:text-white">
          <img src={logo} alt="SWB Logo" className="h-20 w-auto" />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">
              School Wellbeing Staff Portal
            </h1>
          </div>
        </div>

        <div>
          {currentOrgDetails && (
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 p-3 rounded-lg border-l-4 border-violet-400">
              {!referredUser ? (
                <p className="text-sm font-medium text-gray-700">
                  <span className="text-violet-600">School:</span>{" "}
                  {currentOrgDetails.name}
                </p>
              ) : (
                <p className="text-sm font-medium text-gray-700">
                  <span className="text-violet-600">Referred By:</span>{" "}
                  {referredUser.name}
                </p>
              )}
            </div>
          )}
        </div>

        <p
          ref={errRef}
          className={errMsg ? "errmsg text-red-600 text-sm" : "offscreen"}
          aria-live="assertive"
        >
          {errMsg}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="w-full">
            {location?.state?.dependantInfo ? (
              <p>
                <span className="mb-1 text-sm font-medium mr-2 text-gray-700 dark:text-white">
                  Name:
                </span>
                {location?.state?.dependantInfo?.name}
              </p>
            ) : (
              <>
                <label
                  htmlFor="name"
                  className="block mb-2 text-sm font-medium text-gray-700 dark:text-white"
                >
                  Name
                </label>
                <input
                  type="text"
                  ref={emailRef}
                  id="name"
                  name="name"
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Full Name"
                  className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-300 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                />
              </>
            )}
          </div>
          <div className="w-full">
            {location?.state?.dependantInfo ? (
              <p>
                <span className="mb-1 text-sm font-medium mr-2 text-gray-700 dark:text-white">
                  Email:{" "}
                </span>
                {location?.state?.dependantInfo?.email}
              </p>
            ) : (
              <>
                <label
                  htmlFor="email"
                  className="block mb-2 text-sm font-medium text-gray-700 dark:text-white"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Email"
                  className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-300 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                />
              </>
            )}
          </div>
          <div className="w-full">
            {location?.state?.dependantInfo ? (
              <p className="font-normal">
                <span className="mb-1 text-sm font-medium mr-2 text-gray-700 dark:text-white">
                  Date of Birth:{" "}
                </span>
                {moment(location?.state?.dependantInfo?.date_of_birth).format(
                  "DD / MM / YYYY",
                )}
              </p>
            ) : (
              <>
                <label
                  htmlFor="dob"
                  className="block mb-2 text-sm font-medium text-gray-700 dark:text-white"
                >
                  Date of Birth
                </label>
                <div
                  className={`flex w-fit ${dateFocused && "rounded-lg outline-none border-violet-300 ring-1 ring-violet-300"}`}
                >
                  <input
                    ref={dayRef}
                    type="text"
                    id="day"
                    value={day}
                    onChange={handleDayChange}
                    onKeyDown={(e) => handleKeyDown(e, "day")}
                    onFocus={() => setDateFocused(true)}
                    onBlur={() => setDateFocused(false)}
                    placeholder="DD"
                    maxLength="2"
                    required
                    className={`bg-gray-50 border-b border-t border-l text-gray-900 sm:text-sm rounded-l-lg focus:outline-none block w-16 p-2.5 text-center dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white`}
                  />
                  <div
                    className={`bg-gray-50 border-t border-b text-gray-400 font-bold flex items-center justify-center px-2 dark:bg-gray-700`}
                  >
                    /
                  </div>
                  <input
                    ref={monthRef}
                    type="text"
                    id="month"
                    value={month}
                    onChange={handleMonthChange}
                    onKeyDown={(e) => handleKeyDown(e, "month")}
                    onFocus={() => setDateFocused(true)}
                    onBlur={() => setDateFocused(false)}
                    placeholder="MM"
                    maxLength="2"
                    required
                    className={`bg-gray-50 border-t border-b text-gray-900 sm:text-sm focus:outline-none block w-16 p-2.5 text-center dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white`}
                  />
                  <div
                    className={`bg-gray-50 border-t border-b text-gray-400 font-bold flex items-center justify-center px-2 dark:bg-gray-700`}
                  >
                    /
                  </div>
                  <input
                    ref={yearRef}
                    type="text"
                    id="year"
                    value={year}
                    onChange={handleYearChange}
                    onKeyDown={(e) => handleKeyDown(e, "year")}
                    onFocus={() => setDateFocused(true)}
                    onBlur={() => setDateFocused(false)}
                    placeholder="YYYY"
                    maxLength="4"
                    required
                    className={`bg-gray-50 border-b border-t border-r text-gray-900 sm:text-sm rounded-r-lg focus:outline-none block w-20 p-2.5 text-center dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white`}
                  />
                </div>
                {dateErrors.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {dateErrors.map((error, index) => (
                      <p
                        key={index}
                        className="text-red-600 text-sm flex items-center"
                      >
                        <FontAwesomeIcon
                          icon={faCircleExclamation}
                          className="mr-1"
                        />
                        {error}
                      </p>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="relative">
            <label
              htmlFor="pwd"
              className="block mb-2 text-sm font-medium text-gray-700 dark:text-white"
            >
              Password
            </label>
            <input
              type="password"
              id="pwd"
              onChange={(e) => setPwd(e.target.value)}
              onFocus={() => setPwdFocus(true)}
              onBlur={() => setPwdFocus(false)}
              required
              placeholder="Password"
              className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-300 block w-full p-2.5 pr-10 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
            />
            {pwd ? (
              <span
                className={`absolute right-3 top-[42px] ${validPwd ? "text-green-600" : "text-red-600"}`}
                aria-hidden="true"
              >
                <FontAwesomeIcon icon={validPwd ? faCheck : faTimes} />
              </span>
            ) : null}

            <div className="mt-2 text-xs font-medium space-y-1">
              <div
                className={`flex items-center gap-2 ${!pwdHasValue ? "text-gray-400" : pwdChecks.length ? "text-green-600" : "text-red-600"}`}
              >
                <FontAwesomeIcon
                  icon={
                    !pwdHasValue
                      ? faCircleExclamation
                      : pwdChecks.length
                        ? faCheck
                        : faTimes
                  }
                />
                <span>8 to 24 characters</span>
              </div>
              <div
                className={`flex items-center gap-2 ${!pwdHasValue ? "text-gray-400" : pwdChecks.upper ? "text-green-600" : "text-red-600"}`}
              >
                <FontAwesomeIcon
                  icon={
                    !pwdHasValue
                      ? faCircleExclamation
                      : pwdChecks.upper
                        ? faCheck
                        : faTimes
                  }
                />
                <span>At least 1 uppercase letter</span>
              </div>

              <div
                className={`flex items-center gap-2 ${!pwdHasValue ? "text-gray-400" : pwdChecks.number ? "text-green-600" : "text-red-600"}`}
              >
                <FontAwesomeIcon
                  icon={
                    !pwdHasValue
                      ? faCircleExclamation
                      : pwdChecks.number
                        ? faCheck
                        : faTimes
                  }
                />
                <span>At least 1 number</span>
              </div>
              <div
                className={`flex items-center gap-2 ${!pwdHasValue ? "text-gray-400" : pwdChecks.special ? "text-green-600" : "text-red-600"}`}
              >
                <FontAwesomeIcon
                  icon={
                    !pwdHasValue
                      ? faCircleExclamation
                      : pwdChecks.special
                        ? faCheck
                        : faTimes
                  }
                />
                <span>At least 1 special character (! @ # $ %)</span>
              </div>
            </div>
          </div>

          <div className="relative">
            <label
              htmlFor="confirm-pwd"
              className="block mb-2 text-sm font-medium text-gray-700 dark:text-white"
            >
              Confirm Password
            </label>
            <input
              type="password"
              id="confirm-pwd"
              onChange={(e) => setMatchPwd(e.target.value)}
              onFocus={() => setMatchFocus(true)}
              onBlur={() => setMatchFocus(false)}
              required
              placeholder="Confirm Password"
              className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:outline-none focus:border-violet-300 focus:ring-1 focus:ring-violet-300 block w-full p-2.5 pr-10 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
            />
            {matchPwd ? (
              <span
                className={`absolute right-3 top-[42px] ${validMatch ? "text-green-600" : "text-red-600"}`}
                aria-hidden="true"
              >
                <FontAwesomeIcon icon={validMatch ? faCheck : faTimes} />
              </span>
            ) : null}

            <div
              className={`mt-2 text-xs font-medium flex items-center gap-2 ${!matchPwd ? "text-gray-400" : validMatch ? "text-green-600" : "text-red-600"}`}
            >
              <FontAwesomeIcon
                icon={
                  !matchPwd
                    ? faCircleExclamation
                    : validMatch
                      ? faCheck
                      : faTimes
                }
              />
              <span>Passwords match</span>
            </div>
          </div>

          <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-3">
            <input
              id="acceptTerms"
              name="acceptTerms"
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              required
              className="mt-1"
            />
            <label
              htmlFor="acceptTerms"
              className="text-xs text-gray-600 font-medium"
            >
              I agree to the{" "}
              <Link
                to="/terms"
                target="_blank"
                className="text-blue-600 underline"
              >
                Terms and Conditions
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy"
                target="_blank"
                className="text-blue-600 underline"
              >
                Privacy Policy
              </Link>
              .
            </label>
          </div>

          <div className="w-full mt-4">
            <button
              disabled={!canSubmit || creatingAccount} // 1. Automatically disable button while submitting
              className={`flex items-center justify-center w-full text-white font-semibold py-2 px-4 rounded-lg transition text-center mt-4 ${canSubmit && !creatingAccount
                  ? "bg-portal-light hover:bg-portal-medium cursor-pointer"
                  : "bg-gray-300 cursor-not-allowed"
                }`}
            >
              {creatingAccount ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating Account...
                </>
              ) : (
                "Create Account"
              )}
            </button>
          </div>
        </form>

        <div className="mt-6 text-xs text-gray-500 font-medium space-y-2">
          <div>
            <Link to="/login" className="hover:text-violet-400">
              Already have an account? Log in here
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;
